import * as Sentry from '@sentry/react';
import { lazy } from 'react';
import { ErrorPage } from '@pages/ErrorPage';
import { RootLayout } from '../layouts/root-layout';
import { SubRoutingLayout } from '../layouts/sub-routing-layout';
import {
	CompetitionConversionContractor,
	CompetitionConversionGrade,
	CompetitionConversionProduct,
	CompetitionConversionUsage,
} from '@components/competition-conversion/components';
import {
	BasicDetailsForm,
	KYCDetailsForm,
	MoreDetailsForm,
	SubContractorMapping,
} from '@components/endUserDetails/components';
import { AdoptedContractorList, AllEndUserList, AllRigidUserList, LeadList } from '@components/members/components';
import { BankingDetails } from '@components/contractor360/components/contractorNavigation/bankingDetails';
import { RedemptionHistory } from '@components/contractor360/components/redemptionHistory/redemptionHistory';
import { SiteSummary } from '@components/contractor360/components/contractorNavigation/siteSummary';
import { Calendar } from '@components/calendar/calendar';
import { AddNewSite, AddNewMicroSite } from '@components/siteLeads/components';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AllMeetingList, ScheduledMeetingList, TodayMeetingList } from '@components/meetingModule/components';
import { AddNewMeeting } from '@components/meetingModule/components/addNewMeeting/addNewMeeting';
import { Calculator } from '@components/calculator/calculator';
import { MeetingParticipants } from '@components/meetingModule/components/meetingParticipants/meetingParticipants';
import { MeetingDetailsPage } from '@pages/MeetingDetailsPage';
import { TodayMeetingDetailsPage } from '@pages/TodayMeetingDetailsPage';
import { AttendedMembers } from '@components/todayMeetingDetails/components/attendedMembers/attendedmembers';
import { BankingHistory } from '@components/contractor360/components/bankingHistory/bankingHistory';
import { PointsHistory } from '@components/contractor360/components/pointHistory/pointHistory';
import { ConductedMeetingsPage } from '@pages/MeetingsConductedDetailsPage';
import { CompletedMeetings } from '@components/conductedMeetings/completedMeetings/completedMeetings';
import { ExpensiveDetails } from '@components/conductedMeetings/expensiveDetails/expnesiveDetails';
import { QRCodeScanner } from '@components/contractor360/components/qrScanner/qrScanner';
import { QrCodeValidate } from '@components/contractor360/components/qrScanner/components/qrCodeValidate/qrCodeValidate';
import { QRCodeCheck } from '@components/contractor360/components/qrScanner/components/qrCodeCheck/qrcodeCheck';
import { CompletedRecord } from '@components/conductedMeetings/completedRecord/completedRecord';
import { ActivityModule } from '@components/siteLeads/components/activityModule/activityModule';
import { Support } from '@pages/supportpage';
import { SiteLeadAllDetails } from '@pages/SiteLeadAllDetails';
import { AddEditDealerVisit } from '@components/delaerVisit/components/addEditDealerVisit';
import { VisitDetailsPage } from '@pages/VisitDetails';
import { LoginPage } from '@pages/LoginPage';
import { StructuredFeedbackPage } from '@pages/StructuredFeedbackPage';
import { AddEditOneToOneMeetings } from '@components/onetoonemeetings/components/addOneToOneMeeting';
import { AdditionalDetails } from '@components/calculator/components';
import { SupervisorBankingHistory } from '@components/contractor360/components/supervisorBankingHistory/supervisorBankingHistory';
import { SiteSummarySS } from '@components/contractor360/components/contractorNavigation/siteSummarySS';
import { KEASSAddNewSite } from '@components/siteLeads/KessSiteLeads/components/';
import { SiteLeadOverviewKAE } from '@components/contractor360KAE/components/siteLeadOverviewKAE/siteLeadOverviewKAE';
import { StaticDetailsForm } from '@components/contractor360KAE/components/staticDetailsForm/staticDetailsForm';
import { DealerDetails } from '@components/contractor360/components/dealerDetails';
import { ArchitectDetails } from '@components/contractor360/components/architectDetails';
import { FpcStatus } from '@components/contractor360/components/fpcStatus/fpcStatus';
import { FccMetingList } from '@components/meetingModule/components/fcc-meeting/fccMeeting';
import { MdpMeetingList } from '@components/meetingModule/components/mdpMeetings/mdpMeetingList';
import {
	AllSiteList,
	CompetitionSiteList,
	UpcomingSiteList,
	ConversionSiteList,
	PendingLeadsList,
	UpcomingSiteListTaskList,
} from '@components/lms/siteListWrapper/components';
import { SiteInformation, MeetingDetails, GlueDetails, OtherInformation } from '@components/lms/addNewSite/components';
import { SSoLoginPage } from '@pages/SSoLoginPage';
import { AddNewMdpMeeting } from '@components/meetingModule/components/addNewMdpMeeting/addNewMdpMeeting';

const Dashboard = lazy(() =>
	import('@pages/DashboardPage').then((module) => ({
		default: module.DashboardPage,
	}))
);

const EndUsersPage = lazy(() =>
	import('@pages/EndUsersPage').then((module) => ({
		default: module.EndUsersPage,
	}))
);

const EndUserRBDMPage = lazy(() =>
	import('@pages/rbdm/EndUserRBDMPage').then((module) => ({
		default: module.EndUserRBDMPage,
	}))
);

const ProductPerformance = lazy(() =>
	import('@pages/rbdm/ProductPerformance').then((module) => ({
		default: module.ProductPerformance,
	}))
);

const CompetitionConversion = lazy(() =>
	import('@pages/CompetitionConversionPage').then((module) => ({
		default: module.competitionConversionPage,
	}))
);
const Contractor360 = lazy(() =>
	import('@pages/Contractor360KAEPage').then((module) => ({
		default: module.ContractorKAEPage,
	}))
);

const ContractorRBDMPage = lazy(() =>
	import('@pages/rbdm/ContractorRBDMPage').then((module) => ({
		default: module.ContractorRBDMPage,
	}))
);

const StaticDetailsFormPage = lazy(() =>
	import('@pages/rbdm/StaticDetailsFormPage').then((module) => ({
		default: module.StaticDetailsFormPage,
	}))
);

const EndUserDetails = lazy(() =>
	import('@pages/EndUserDetailsPage').then((module) => ({ default: module.EndUserDetailsPage }))
);

const MeetingListing = lazy(() =>
	import('@pages/MeetingModulePage').then((module) => ({ default: module.MeetingModulePage }))
);

const SiteLeads = lazy(() =>
	import('@pages/SiteLeadsPage').then((module) => ({
		default: module.SiteLeadsPage,
	}))
);

const SiteSummaryPage = lazy(() =>
	import('@pages/SiteSummaryPage').then((module) => ({
		default: module.SiteSummaryPage,
	}))
);

const Gallery = lazy(() =>
	import('@pages/GalleryPage').then((module) => ({
		default: module.GalleryModulePage,
	}))
);

const Profile = lazy(() =>
	import('@pages/ProfilePage').then((module) => ({
		default: module.ProfilePage,
	}))
);
const EndUserNorms = lazy(() =>
	import('@pages/EndUserNormsPage').then((module) => ({
		default: module.EndUserNormsPage,
	}))
);

const SiteLeadOverview = lazy(() =>
	import('@pages/SiteLeadOverview').then((module) => ({
		default: module.siteLeadOverview,
	}))
);

const DealerVisit = lazy(() =>
	import('@pages/DelaerVisitPage').then((module) => ({
		default: module.DelaerVisitPage,
	}))
);

const PCMSPage = lazy(() =>
	import('@pages/PcmsPage').then((module) => ({
		default: module.PcmsPage,
	}))
);

// TODO: Need to confirm with client for the One to one meetings module

const OneToOneMeetingsPage = lazy(() =>
	import('@pages/OneToOneMeeetingsPage').then((module) => ({
		default: module.OneToOneMeetingsPage,
	}))
);

// TODO: Need to confirm with client for the FM Content module

const FieldMarketingContent = lazy(() =>
	import('@pages/FieldMarketingContentPage').then((module) => ({
		default: module.FieldMarketingContentPage,
	}))
);

const SupervisorList = lazy(() =>
	import('@pages/SupervisorListPage').then((module) => ({
		default: module.SupervisorListPage,
	}))
);

const SupervisorListRBDMPage = lazy(() =>
	import('@pages/rbdm/SupervisorListRBDMPage').then((module) => ({
		default: module.SupervisorListRBDMPage,
	}))
);

const SupervisorDetails = lazy(() =>
	import('@pages/SupervisorDetailsPage').then((module) => ({
		default: module.SupervisorDetailsPage,
	}))
);

const FactoryList = lazy(() =>
	import('@pages/FactoryListPage').then((module) => ({
		default: module.FactoryListPage,
	}))
);

const FactoryDetails = lazy(() =>
	import('@pages/FactoryDetailsPage').then((module) => ({
		default: module.FactoryDetailsPage,
	}))
);

const JharokhaPage = lazy(() =>
	import('@pages/JharokhaPage').then((module) => ({
		default: module.jharokhaPage,
	}))
);

const KAESSSiteLeadsPage = lazy(() =>
	import('@pages/KeassSiteLeadsPage').then((module) => ({
		default: module.KAESSSiteLeadsPage,
	}))
);

const KAESSSiteSummaryPage = lazy(() =>
	import('@pages/SiteSummaryPage').then((module) => ({
		default: module.SiteSummaryKAESSPage,
	}))
);
const SiteSpecialistSiteSummaryPage = lazy(() =>
	import('@pages/SiteSummaryPage').then((module) => ({
		default: module.SiteSpecialistSiteSummaryPage,
	}))
);

const UsersPage = lazy(() =>
	import('@pages/lms/UsersPage').then((module) => ({
		default: module.UsersPage,
	}))
);

const Contractor360Page = lazy(() =>
	import('@pages/lms/Contractor360Page').then((module) => ({
		default: module.Contractor360Page,
	}))
);

const SiteListPage = lazy(() =>
	import('@pages/lms/SiteListPage').then((module) => ({
		default: module.SiteListPage,
	}))
);

const MapviewPage = lazy(() =>
	import('@pages/lms/MapviewPage').then((module) => ({
		default: module.MapviewPage,
	}))
);

const AddNewSitePage = lazy(() =>
	import('@pages/lms/AddNewSitePage').then((module) => ({ default: module.AddNewSitePage }))
);

const SiteOverviewPage = lazy(() =>
	import('@pages/lms/SiteOverviewPage').then((module) => ({
		default: module.SiteOverviewPage,
	}))
);

const SiteVisitPage = lazy(() =>
	import('@pages/lms/SiteVisitPage').then((module) => ({
		default: module.SiteVisitPage,
	}))
);

const MyTaskListPage = lazy(() =>
	import('@pages/lms/MyTaskListPage').then((module) => ({
		default: module.MyTaskListPage,
	}))
);
const ProductRecommendationsPage = lazy(() =>
	import('@pages/lms/ProductRecommendationsPage').then((module) => ({
		default: module.ProductRecommendationsPage,
	}))
);

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
	{
		path: '/login',
		errorElement: <ErrorPage />,
		children: [
			{
				element: <LoginPage />,
				index: true,
			},
		],
	},
	{
		path: 'sso-login/',
		errorElement: <ErrorPage />,
		children: [
			{
				element: <SSoLoginPage />,
				index: true,
			},
		],
	},
	{
		path: '/',
		element: <RootLayout />,
		errorElement: <ErrorPage />,
		children: [
			{ index: true, element: <Dashboard /> },
			{
				path: '/end-users',
				element: <EndUsersPage />,
				children: [
					{
						index: true,
						element: <AdoptedContractorList />,
					},
					{
						path: '/end-users/new-leads',
						element: <LeadList />,
					},
					{
						path: '/end-users/all',
						element: <AllEndUserList />,
					},
				],
			},
			{
				path: '/site-summary',
				element: <SiteSummaryPage />,
			},
			{
				path: '/site-summary/new-leads',
				element: <KAESSSiteSummaryPage />,
			},
			{
				path: '/site-summary/site-specialist-sites',
				element: <SiteSpecialistSiteSummaryPage />,
			},
		],
	},

	{
		path: '/profile',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <Profile />,
			},
		],
	},

	{
		path: '/competition-conversion',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/competition-conversion',
				element: <CompetitionConversion />,
				children: [
					{
						index: true,
						element: <CompetitionConversionUsage />,
					},
					{
						path: '/competition-conversion/type-of-contractor',
						element: <CompetitionConversionContractor />,
					},
					{
						path: '/competition-conversion/grade-of-competition',
						element: <CompetitionConversionGrade />,
					},
					{
						path: '/competition-conversion/products-recommended',
						element: <CompetitionConversionProduct />,
					},
				],
			},
		],
	},

	{
		path: '/',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/meetings',
				element: <MeetingListing />,
				children: [
					{
						index: true,
						element: <ScheduledMeetingList />,
					},
					{
						path: '/meetings/todays-meetings',
						element: <TodayMeetingList />,
					},
					{
						path: '/meetings/all-meetings',
						element: <AllMeetingList />,
					},
					{
						path: '/meetings/fcc-meetings',
						element: <FccMetingList />,
					},
					{
						path: '/meetings/mdp',
						element: <MdpMeetingList />,
					},
				],
			},
			{
				path: '/dealer-visit-summary',
				element: <DealerVisit />,
			},
			{
				path: '/gallery',
				element: <Gallery />,
			},
			{
				path: '/field-marketing-content',
				element: <FieldMarketingContent />,
			},
			{
				path: '/pcms',
				element: <PCMSPage />,
			},
			{
				path: '/help-and-support',
				element: <Support />,
			},
			{
				path: '/one-to-one-meeting-summary',
				element: <OneToOneMeetingsPage />,
			},
			{
				path: '/supervisor',
				element: <SupervisorList />,
			},
			{
				path: '/factory-visit',
				element: <FactoryList />,
			},
			{
				path: '/product-performance',
				element: <ProductPerformance />,
			},
		],
	},

	{
		path: '/supervisor',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/supervisor/add-supervisor',
				element: <SupervisorDetails />,
			},
			{
				path: '/supervisor/edit-supervisor',
				element: <SupervisorDetails />,
			},
		],
	},

	{
		path: '/today-meetings',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <TodayMeetingDetailsPage />,
			},
			{
				path: '/today-meetings/attendance-details',
				element: <AttendedMembers />,
			},
		],
	},

	{
		path: '/meetings-conducted',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/meetings-conducted',
				element: <ConductedMeetingsPage />,
				children: [
					{
						index: true,
						element: <CompletedMeetings />,
					},
					{
						path: '/meetings-conducted/expense-details',
						element: <ExpensiveDetails />,
					},
					{
						path: '/meetings-conducted/completed-record',
						element: <CompletedRecord />,
					},
				],
			},
		],
	},

	{
		path: '/meeting-details',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <MeetingDetailsPage />,
			},
			{
				path: '/meeting-details/manage-participants',
				element: <MeetingParticipants />,
			},
		],
	},

	{
		path: '/add-new-meeting',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/add-new-meeting',
				element: <AddNewMeeting />,
			},
			{
				path: '/add-new-meeting/manage-participants',
				element: <MeetingParticipants />,
			},
		],
	},

	{
		path: '/edit-meeting',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/edit-meeting',
				element: <AddNewMeeting />,
			},
			{
				path: '/edit-meeting/manage-participants',
				element: <MeetingParticipants />,
			},
		],
	},

	{
		path: '/add-member-details',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/add-member-details',
				element: <EndUserDetails />,
				children: [
					{
						path: '/add-member-details/basic-details',
						element: <BasicDetailsForm />,
					},
					{
						path: '/add-member-details/kyc-details',
						element: <KYCDetailsForm />,
					},
					{
						path: '/add-member-details/more-details',
						element: <MoreDetailsForm />,
					},
					{
						path: '/add-member-details/sub-contractor-details',
						element: <SubContractorMapping />,
					},
				],
			},
		],
	},

	{
		path: '/edit-member-details',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/edit-member-details',
				element: <EndUserDetails />,
				children: [
					{
						path: '/edit-member-details/basic-details',
						element: <BasicDetailsForm />,
					},
					{
						path: '/edit-member-details/kyc-details',
						element: <KYCDetailsForm />,
					},
					{
						path: '/edit-member-details/more-details',
						element: <MoreDetailsForm />,
					},
					{
						path: '/edit-member-details/sub-contractor-details',
						element: <SubContractorMapping />,
					},
				],
			},
		],
	},

	{
		path: '/contractor',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			// {
			// 	index: true,
			// 	element: <Contractor360 />,
			// },
			{
				index: true,
				element: <Contractor360 />,
			},
			{
				path: '/contractor/static-details',
				element: <StaticDetailsForm />,
			},
			{
				path: '/contractor/site-lead-overview',
				element: <SiteLeadOverviewKAE />,
			},
			{
				path: '/contractor/banking-details',
				element: <BankingDetails />,
			},
			{
				path: '/contractor/calculator',
				element: <Calculator />,
			},
			{
				path: '/contractor/calculator/calculator-details',
				element: <AdditionalDetails />,
			},
			{
				path: '/contractor/banking-history',
				element: <BankingHistory />,
			},
			{
				path: '/contractor/points-statement',
				element: <PointsHistory />,
			},
			{
				path: '/contractor/redemption-history',
				element: <RedemptionHistory />,
			},
			{
				path: '/contractor/qr-code-scanner',
				element: <QRCodeScanner />,
			},
			{
				path: '/contractor/qr-code-check',
				element: <QRCodeCheck />,
			},
			{
				path: '/contractor/qr-code-validate',
				element: <QrCodeValidate />,
			},
			{
				path: '/contractor/site-summary',
				element: <SiteSummary />,
			},
			{
				path: '/contractor/site-specialist-site-summary',
				element: <SiteSummarySS />,
			},
			{
				path: '/contractor/supervisor-summary',
				element: <SupervisorList />,
			},
			{
				path: '/contractor/sub-contractor-details',
				element: <SubContractorMapping />,
			},
			{
				path: '/contractor/supervisor-banking-history',
				element: <SupervisorBankingHistory />,
			},
			{
				path: '/contractor/dealer-details',
				element: <DealerDetails />,
			},
			{
				path: '/contractor/architect-details',
				element: <ArchitectDetails />,
			},
			{
				path: '/contractor/fpc-status',
				element: <FpcStatus />,
			},
			{
				path: '/contractor/ss-site-details',
				element: <AddNewMicroSite />,
			},
		],
	},

	{
		path: '/calendar',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <Calendar />,
			},
		],
	},

	{
		path: '/add-new-site',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/add-new-site',
				element: <SiteLeads />,
				children: [
					{
						index: true,
						element: <AddNewSite />,
					},
				],
			},
		],
	},

	{
		path: '/activity-module',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <ActivityModule />,
			},
		],
	},

	{
		path: '/user-sufficiency-summary',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <EndUserNorms />,
			},
		],
	},

	{
		path: '/site-lead-overview',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <SiteLeadOverview />,
			},
			{
				path: '/site-lead-overview/site-details',
				element: <SiteLeadAllDetails />,
			},
			{
				path: '/site-lead-overview/visit-details',
				element: <VisitDetailsPage />,
			},
		],
	},

	{
		path: '/add-dealer-visit',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <AddEditDealerVisit />,
			},
		],
	},

	{
		path: '/edit-dealer-visit',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <AddEditDealerVisit />,
			},
		],
	},
	{
		path: '/add-one-to-one-meeting',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <AddEditOneToOneMeetings />,
			},
		],
	},
	{
		path: '/edit-one-to-one-connect',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <AddEditOneToOneMeetings />,
			},
		],
	},
	{
		path: '/structured-questionnaires',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				index: true,
				element: <StructuredFeedbackPage />,
			},
		],
	},

	{
		path: '/factory-visit',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/factory-visit/add-factory-visit',
				element: <FactoryDetails />,
			},
			{
				path: '/factory-visit/view-factory-visit',
				element: <FactoryDetails />,
			},
		],
	},
	{
		path: '/jharokha',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/jharokha',
				element: <JharokhaPage />,
			},
		],
	},
	{
		path: '/add-new-site-ss',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/add-new-site-ss',
				element: <KAESSSiteLeadsPage />,
				children: [
					{
						index: true,
						element: <KEASSAddNewSite />,
					},
				],
			},
		],
	},
	{
		path: '/add-new-micro-site',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/add-new-micro-site',
				element: <AddNewMicroSite />,
				children: [
					{
						index: true,
						element: <AddNewMicroSite />,
					},
				],
			},
		],
	},

	{
		path: 'lms',
		errorElement: <ErrorPage />,
		children: [
			// Routes using RootLayout
			{
				element: <RootLayout />,
				errorElement: <ErrorPage />,
				children: [
					{
						index: true,
						element: <Dashboard />,
					},
					{
						path: 'sites',
						element: <SiteListPage />,
						children: [
							{
								index: true,
								element: <AllSiteList />,
							},
							{
								path: 'competition',
								element: <CompetitionSiteList />,
							},
							{
								path: 'upcoming',
								element: <UpcomingSiteList />,
							},
						],
					},
					{
						path: 'mapview',
						element: <MapviewPage />,
					},
					{
						path: 'my-tasklist',
						element: <MyTaskListPage />,
						children: [
							{
								index: true,
								element: <Navigate to="conversion" replace />,
							},
							{
								path: 'conversion',
								element: <ConversionSiteList />,
							},
							{
								path: 'pending-leads',
								element: <PendingLeadsList />,
							},
							{
								path: 'upcoming',
								element: <UpcomingSiteListTaskList />,
							},
						],
					},
				],
			},

			// Routes using SubRoutingLayout (no footer)
			{
				element: <SubRoutingLayout />,
				errorElement: <ErrorPage />,
				children: [
					{
						path: 'users',
						element: <UsersPage />,
					},
					{
						path: 'new-leads',
						element: <UsersPage />,
					},
					{
						path: 'contractor',
						element: <Contractor360Page />,
					},
					{
						path: 'add-new-site',
						element: <AddNewSitePage />,
						children: [
							{
								index: true,
								element: <Navigate to="site-information" replace />,
							},
							{
								path: 'site-information',
								element: <SiteInformation />,
							},
							{
								path: 'meeting-details',
								element: <MeetingDetails />,
							},
							{
								path: 'glue-details',
								element: <GlueDetails />,
							},
							{
								path: 'other-information',
								element: <OtherInformation />,
							},
						],
					},
					{
						path: 'product-recommendations',
						element: <ProductRecommendationsPage />,
					},
					{
						path: 'site-overview',
						element: <SiteOverviewPage />,
					},
					{
						path: 'site-visit',
						element: <SiteVisitPage />,
					},
					{
						path: 'visit-details',
						element: <VisitDetailsPage />,
					},
				],
			},
		],
	},
	{
		path: '/rbdm',
		errorElement: <ErrorPage />,
		children: [
			// Routes using RootLayout
			{
				element: <RootLayout />,
				errorElement: <ErrorPage />,
				children: [
					{
						index: true,
						element: <Dashboard />,
					},
					{
						path: '/rbdm/end-users',
						element: <EndUserRBDMPage />,
						children: [
							{
								path: '/rbdm/end-users/all',
								element: <AllEndUserList />,
							},
							{
								path: '/rbdm/end-users/all-rigid',
								element: <AllRigidUserList />,
							},
						],
					},
				],
			},

			{
				path: '/rbdm/meetings',
				element: <SubRoutingLayout />,
				errorElement: <ErrorPage />,
				children: [
					{
						path: '/rbdm/meetings',
						element: <MeetingListing />,
						children: [
							{
								index: true,
								element: <ScheduledMeetingList />,
							},
							{
								path: '/rbdm/meetings/todays-meetings',
								element: <TodayMeetingList />,
							},
							{
								path: '/rbdm/meetings/all-meetings',
								element: <AllMeetingList />,
							},
							{
								path: '/rbdm/meetings/mdp',
								element: <MdpMeetingList />,
							},
						],
					},
				],
			},

			// Routes using SubRoutingLayout (no footer)
			{
				path: '/rbdm/contractor',
				element: <SubRoutingLayout />,
				errorElement: <ErrorPage />,
				children: [
					{
						index: true,
						element: <ContractorRBDMPage />,
					},
					{
						path: '/rbdm/contractor/static-details',
						element: <StaticDetailsFormPage />,
					},
				],
			},

			{
				path: '/rbdm/supervisor',
				element: <SubRoutingLayout />,
				errorElement: <ErrorPage />,
				children: [
					{
						index: true,
						element: <SupervisorListRBDMPage />,
					},
				],
			},

			{
				path: '/rbdm/add-new-meeting',
				errorElement: <ErrorPage />,
				element: <SubRoutingLayout />,
				children: [
					{
						path: '/rbdm/add-new-meeting',
						element: <AddNewMeeting />,
					},
				],
			},

			{
				path: '/rbdm/today-meetings',
				errorElement: <ErrorPage />,
				element: <SubRoutingLayout />,
				children: [
					{
						index: true,
						element: <TodayMeetingDetailsPage />,
					},
					{
						path: '/rbdm/today-meetings/attendance-details',
						element: <AttendedMembers />,
					},
				],
			},

			{
				path: '/rbdm/site-summary',
				element: <RootLayout />,
				errorElement: <ErrorPage />,
				children: [
					{
						index: true,
						element: <SiteSummaryPage />,
					},
				],
			},
			{
				path: '/rbdm/site-lead-overview',
				errorElement: <ErrorPage />,
				element: <SubRoutingLayout />,
				children: [
					{
						index: true,
						element: <SiteLeadOverview />,
					},
					{
						path: '/rbdm/site-lead-overview/site-details',
						element: <SiteLeadAllDetails />,
					},
					{
						path: '/rbdm/site-lead-overview/visit-details',
						element: <VisitDetailsPage />,
					},
				],
			},
		],
	},
	{
		path: '/rbdm/add-new-mdp-meeting',
		errorElement: <ErrorPage />,
		element: <SubRoutingLayout />,
		children: [
			{
				path: '/rbdm/add-new-mdp-meeting',
				element: <AddNewMdpMeeting />,
			},
		],
	},
]);
